import { compareBiomeDetails } from '~app/actions/biomeDetails';
import { loadLocationInfo } from '~app/actions/database';
import { getFavoriteLocationInfo } from '~app/actions/favorites';
import * as Geocode from '~app/actions/geocoder';
import { flyTo } from '~app/actions/globe';

import createBillboard from '~app/core/createBillboard';
import defined from '~app/core/defined';
import Location from '~app/core/location';

export const DROP_PIN = 'DROP_PIN';

export const CLEAR_PIN = 'CLEAR_PIN';
export const CLEAR_PINS = 'CLEAR_PINS';

export const SELECT_LOCATION = 'SELECT_LOCATION';
export const SELECT_LOCATION_INFO = 'SELECT_LOCATION_INFO';
export const CLEAR_SELECTION = 'CLEAR_SELECTION';

export const SET_SELECTION_MODE = 'SET_SELECTION_MODE';

export const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
export const GEOCODE_SEARCH = 'GEOCODE_SEARCH';

// Pins

const searchPinImage = require('~app/resources/images/billboards/Pin_Red.png');

export function dropPin(location) {
  return async (dispatch) => {
    // Request that the data be loaded, but don't await the promise so
    // we don't block unnecessarily. Any client that needs the data should
    // be aware that they need to await to get the data.
    const locationInfoPromise = dispatch(loadLocationInfo(location));

    // Drop the pin immediately, even before the data associated with the
    // location is loaded, to increase responsiveness.
    dispatch({
      type: DROP_PIN,
      marker: createBillboard(location, searchPinImage, {verticalOrigin: "bottom"}),
      location,
      locationInfoPromise,
    });

    return locationInfoPromise;
  };
}

export function dropPins(locations) {
  return (dispatch) => {
    // Return an array of promises.
    // I'm not using Promise.all so that the caller can wait on just the ones
    // they need and be as responsive as possible.
    return locations.map(location => dispatch(dropPin(location)));
  };
}

export function clearPin(location) {
  return (dispatch, getState) => {
    // Look up the marker instance corresponding to this location.
    const state = getState();
    const index = state.search.searchLocations.indexOf(location);
    if (index >= 0) {
      const marker = state.search.searchMarkers[index];
      dispatch({
        type: CLEAR_PIN,
        marker,
      });
    }
  };
}

export function clearPins() {
  return {
    type: CLEAR_PINS,
  };
}

export function selectPin(marker) {
  return (dispatch, getState) => {
    const selectionMode = getState().search.selectionMode;
    switch(selectionMode) {
      case SelectionMode.select:
        dispatch(executeSelectionPinSelect(marker));
        break;
      case SelectionMode.compareLeft:
        dispatch(executeComparePinSelect(marker, 0));
        break;
      case SelectionMode.compareRight:
      dispatch(executeComparePinSelect(marker, 1));
        break;
      default:
        throw new Error(`Unexpected SelectionMode: ${selectionMode}`);
    }
  };
}

function executeSelectionPinSelect(marker) {
  return async (dispatch, getState) => {
    // Look up the Location instance corresponding to this marker.
    const state = getState();
    const index = state.search.searchMarkers.indexOf(marker);
    if (index >= 0) {
      dispatch(selectLocation(state.search.searchLocations[index]));

      const locationInfo = await state.search.locationInfoPromises[index];
      dispatch(selectLocationInfo(locationInfo));
    }
  };
}

function executeComparePinSelect(marker, compareIndex) {
  return async (dispatch, getState) => {
    // Look up the Location instance corresponding to this marker.
    const state = getState();
    const index = state.search.searchMarkers.indexOf(marker);
    if (index >= 0) {
      const locationInfo = await state.search.locationInfoPromises[index];

      dispatch(executeComparison(locationInfo, compareIndex));
    }
  };
}

export function selectFavorite(marker) {
  return (dispatch, getState) => {
    const selectionMode = getState().search.selectionMode;
    switch(selectionMode) {
      case SelectionMode.select:
        dispatch(executeSelectionFavoriteSelect(marker));
        break;
      case SelectionMode.compareLeft:
        dispatch(executeCompareFavoriteSelect(marker, 0));
        break;
      case SelectionMode.compareRight:
        dispatch(executeCompareFavoriteSelect(marker, 1));
        break;
      default:
        throw new Error(`Unexpected SelectionMode: ${selectionMode}`);
    }
  };
}

function executeSelectionFavoriteSelect(marker) {
  return (dispatch, getState) => {
    // Look up the Location instance corresponding to this marker.
    const state = getState();
    const index = state.favorites.favoriteMarkers.indexOf(marker);
    if (index >= 0) {
      const locationInfo = state.favorites.locationInfos[index];
      dispatch(selectLocation(locationInfo.location));
      dispatch(selectLocationInfo(locationInfo));
    }
  };
}

function executeCompareFavoriteSelect(marker, compareIndex) {
  return (dispatch, getState) => {
    // Look up the Location instance corresponding to this marker.
    const state = getState();
    const index = state.favorites.favoriteMarkers.indexOf(marker);
    if (index >= 0) {
      const locationInfo = state.favorites.locationInfos[index];

      dispatch(executeComparison(locationInfo, compareIndex));
    }
  };
}

export function selectLocation(location) {
  return {
    type: SELECT_LOCATION,
    location,
  };
}

export function selectLocationInfo(locationInfo) {
  return {
    type: SELECT_LOCATION_INFO,
    locationInfo,
  };
}

export function clearSelection() {
  return {
    type: CLEAR_SELECTION,
  };
}

// Comparison

export const SelectionMode = Object.freeze({
  select: 1,
  compareLeft: 2,
  compareRight: 3,
});

export function enterCompareMode(mode) {
  if (mode !== SelectionMode.compareLeft &&
      mode !== SelectionMode.compareRight) {
    throw new Error(`Invalid comparison mode: ${mode}`);
  }

  return {
    type: SET_SELECTION_MODE,
    mode,
  };
}

export function exitCompareMode() {
  return {
    type: SET_SELECTION_MODE,
    mode: SelectionMode.select,
  };
}

// Geocoder

export function setSearchTerm(searchTerm) {
  return {
    type: SET_SEARCH_TERM,
    searchTerm,
  };
}

export function clearSearchTerm() {
  return  {
    type: SET_SEARCH_TERM,
    searchTerm: null,
  };
}

export function executeSearchQuery(query) {
  return async (dispatch, getState) => {
    // If the query is not defined, it means that the search bar has been
    // cleared and should lose focus, which is different from an empty string
    // that indicates the user is just beginning to add text.
    // But if you pass a null value to the geocoder, it searches for the string
    // "null", which is not intended. So set the query string to empty.
    if (!defined(query)) {
      query = '';
    }

    const {isPointQuery, lat, lon} = parsePointQuery(query);
    let locations = undefined;
    if (isPointQuery) {
      const rawResults = await dispatch(Geocode.reverseGeocode(lat, lon));
      locations = [processReverseResult(rawResults[0], lat, lon)];
    }
    else {
      const rawResults = await dispatch(Geocode.forwardGeocode(query));
      locations = rawResults.map(processForwardResult);
    }

    const selectionMode = getState().search.selectionMode;
    switch(selectionMode) {
      case SelectionMode.select:
        dispatch(executeSelectionSearchQuery(locations));
        break;
      case SelectionMode.compareLeft:
        dispatch(executeCompareSearchQuery(locations, 0));
        break;
      case SelectionMode.compareRight:
        dispatch(executeCompareSearchQuery(locations, 1));
        break;
      default:
        throw new Error(`Unexpected SelectionMode: ${selectionMode}`);
    }
  };
}

function executeSelectionSearchQuery(locations) {
  return async (dispatch, getState) => {
    dispatch(clearSelection());
    dispatch(clearPins());

    if (defined(locations) && locations.length > 0) {
      const location = locations[0];
      dispatch(selectLocation(location));

      dispatch(centerLocation(location));

      const favorites = getState().favorites.locationInfos;
      const favoriteLocationInfo = getFavoriteLocationInfo(location, favorites);
      if (defined(favoriteLocationInfo)) {
        dispatch(selectLocationInfo(favoriteLocationInfo));
      }
      else {
        const loadingPromises = dispatch(dropPins(locations));

        // Only display content related to that location once it is available.
        const locationInfo = await loadingPromises[0];
        dispatch(selectLocationInfo(locationInfo));
      }
    }
  };
}

function executeCompareSearchQuery(locations, compareIndex) {
  return async (dispatch, getState) => {
    if (defined(locations) && locations.length > 0) {
      const location = locations[0];
      let locationInfo = null;

      const favorites = getState().favorites.locationInfos;
      const favoriteLocationInfo = getFavoriteLocationInfo(location, favorites);
      if (defined(favoriteLocationInfo)) {
        locationInfo = favoriteLocationInfo;
      }
      else {
        locationInfo = await dispatch(loadLocationInfo(location));
      }

      dispatch(executeComparison(locationInfo, compareIndex));
    }
  };
}

export function executeTapQuery(location) {
  return (dispatch, getState) => {
    const selectionMode = getState().search.selectionMode;
    switch(selectionMode) {
      case SelectionMode.select:
        dispatch(executeSelectionTapQuery(location));
        break;
      case SelectionMode.compareLeft:
        dispatch(executeCompareTapQuery(location, 0));
        break;
      case SelectionMode.compareRight:
        dispatch(executeCompareTapQuery(location, 1));
        break;
      default:
        throw new Error(`Unexpected SelectionMode: ${selectionMode}`);
    }
  };
}

function executeSelectionTapQuery(location) {
  return async (dispatch) => {
    dispatch(clearSelection());
    dispatch(clearPins());

    const loadingPromise = dispatch(dropPin(location));

    dispatch(selectLocation(location));

    // Only display content related to that location once it is available.
    const locationInfo = await loadingPromise;
    dispatch(selectLocationInfo(locationInfo));
  };
}

function executeCompareTapQuery(location, compareIndex) {
  return async (dispatch, getState) => {
    const locationInfo = await dispatch(loadLocationInfo(location));
    dispatch(executeComparison(locationInfo, compareIndex));
  };
}

function executeComparison(locationInfo, compareIndex) {
  return (dispatch, getState) => {
    const locationInfos = [getState().search.selectedLocationInfo];
    locationInfos.splice(compareIndex, 0, locationInfo);

    dispatch(compareBiomeDetails(locationInfos));
  };
}

// Flight

export function centerLocation(location, {
    duration = undefined,
    height = 15000000,
  } = {}) {

  return (dispatch) => {
    const coordinates = [...location.coordinates(), height];
    dispatch(flyTo(new Location(coordinates, location.displayName), duration));
  };
}

// Search query helpers

function parsePointQuery(query) {
  // Regex help:
  // Floating point number: [+-]?\d*(?:\.\d+)?
  // Whitespace: \s*
  // Latitude modifier: [nNsS]
  // Longitude modifier: [eEwW]
  const regex = /^\s*([+-]?\d*(?:\.\d+)?)\s*([nNsS]?)\s*,\s*([+-]?\d*(?:\.\d+)?)\s*([eEwW]?)\s*$/;
  const matches = regex.exec(query);
  let isPointQuery = defined(matches) && (matches.length === 5);
  let lat = undefined;
  let lon = undefined;
  if (isPointQuery) {
    const [_lat, latModifier, _lon, lonModifier] = matches.slice(1);
    lat = _lat;
    lon = _lon;

    if (isPointQuery && lat >= -90.0 && lat <= 90.0) {
      if (latModifier === 'n' || latModifier === 'N') {
        // If a positive latitude is marked with north, record that value.
        // Otherwise, the query is invalid because a negative latitude cannot be
        // marked as north.
        if (lat < 0.0) {
          isPointQuery = false;
        }
      }
      else if (latModifier === 's' || latModifier === 'S') {
        // If a positive latitude is marked as south, negate it.
        // Otherwise, allow a negative latitude to be redundantly marked as south.
        if (lat > 0.0) {
          lat = -lat;
        }
      }
    }

    if (isPointQuery && lon >= -180.0 && lon <= 180.0) {
      if (lonModifier === 'e' || lonModifier === 'E') {
        // If a positive longitude is marked with east, record that value.
        // Otherwise, the query is invalid because a negative longitude cannot be
        // marked as east.
        if (lon < 0.0) {
          isPointQuery = false;
        }
      }
      else if (lonModifier === 'w' || lonModifier === 'W') {
        // If a positive longitude is marked as west, negate it.
        // Otherwise, allow a negative longitude to be redundantly marked as west.
        if (lon > 0.0) {
          lon = -lon;
        }
      }
    }
  }

  return {
    isPointQuery,
    lat,
    lon,
  };
}

function processForwardResult(rawResult) {
  return new Location(
    rawResult.coordinates,
    getDisplayName(rawResult),
  );
}

function processReverseResult(rawResult, lat, lon) {
  // For reverse geocodes, we are only interested in the display name.
  // The coordinates come directly from the query, not from the geocoding result.
  return new Location(
    [Number(lon) * Math.PI / 180.0, Number(lat) * Math.PI / 180.0],
    getDisplayName(rawResult),
  );
}

function getDisplayName(rawResult) {
  let displayName = undefined;
  if (defined(rawResult) && defined(rawResult.displayName)) {
    displayName = rawResult.displayName;
  }
  return displayName;
}



// WEBPACK FOOTER //
// ./src/actions/search.js